import React, { useEffect, useMemo, useState } from "react";
import { MessageCircle, CheckCircle2, RotateCcw } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { inr } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { weekRange, fmt, message, waUrl } from "@/components/WeeklySummaryDialog";

const sentKey = (uid, fk) => `ct_weekly_sent_${uid}_${fk}`;

export default function WeeklySend() {
  const { user } = useAuth();
  const [offset, setOffset] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [hideZero, setHideZero] = useState(true);
  const range = useMemo(() => weekRange(offset), [offset]);
  const [sent, setSent] = useState({});

  useEffect(() => {
    setLoading(true);
    api.get("/weekly-summaries", { params: { fk: range[0], tk: range[1] } }).then(({ data }) => setRows(data)).catch(() => setRows([])).finally(() => setLoading(false));
    try { setSent(JSON.parse(localStorage.getItem(sentKey(user?.id, range[0])) || "{}")); } catch { setSent({}); }
  }, [range, user?.id]);

  const mark = (id, v) => {
    const next = { ...sent, [id]: v };
    if (!v) delete next[id];
    setSent(next);
    localStorage.setItem(sentKey(user?.id, range[0]), JSON.stringify(next));
  };

  const ql = q.toLowerCase();
  const shown = rows.filter((r) => (!hideZero || r.summary.days > 0) && (!q || `${r.full_name} ${r.fe_id} ${r.mobile}`.toLowerCase().includes(ql)));
  const sentCount = shown.filter((r) => sent[r.id]).length;

  return (
    <div data-testid="weekly-send-page">
      <PageHeader title="Bulk Weekly Send" subtitle="Send every Delivery Partner their weekly summary on WhatsApp — one tap per DP." />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-border p-1 text-sm">
          {["This Week", "Last Week"].map((l, i) => (
            <button key={l} onClick={() => setOffset(i)} data-testid={`bulk-weekly-${i ? "last" : "this"}`}
              className={cn("rounded-md px-4 py-1.5 transition-colors", offset === i ? "bg-primary text-primary-foreground" : "hover:bg-accent")}>{l}</button>
          ))}
        </div>
        <span className="text-sm text-muted-foreground" data-testid="bulk-weekly-range">{fmt(range[0])} – {fmt(range[1])}</span>
        <Input className="max-w-xs" placeholder="Search name, FE ID, phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bulk-weekly-search" />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="bulk-weekly-hide-zero" /> Only DPs active this week</label>
        <span className="ml-auto rounded-full bg-emerald-500/10 px-3 py-1 text-sm font-semibold text-emerald-500" data-testid="bulk-weekly-progress">{sentCount} / {shown.length} sent</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Delivery Partner</TableHead><TableHead>Franchise</TableHead>
            <TableHead className="text-right">Days</TableHead><TableHead className="text-right">Orders</TableHead>
            <TableHead className="text-right">Multi</TableHead><TableHead className="text-right">Earnings</TableHead><TableHead className="text-right">Send</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && shown.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No Delivery Partners to show for this week</TableCell></TableRow>}
            {!loading && shown.map((r) => {
              const s = r.summary;
              const done = !!sent[r.id];
              return (
                <TableRow key={r.id} data-testid={`bulk-row-${r.fe_id}`} className={cn(done && "opacity-60")}>
                  <TableCell><p className="font-semibold">{r.full_name}</p><p className="font-mono text-xs text-muted-foreground">{r.fe_id} · {r.mobile || "no mobile"}</p></TableCell>
                  <TableCell className="text-sm">{r.franchise_partner_name || "Direct"}</TableCell>
                  <TableCell className="text-right tabular">{s.days}</TableCell>
                  <TableCell className="text-right tabular font-semibold">{s.orders}</TableCell>
                  <TableCell className="text-right tabular text-sky-500">{s.multi}</TableCell>
                  <TableCell className="text-right tabular font-semibold text-emerald-500">{inr(s.earn)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {done && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Mark as not sent" onClick={() => mark(r.id, false)} data-testid={`bulk-undo-${r.fe_id}`}><RotateCcw className="h-4 w-4" /></Button>}
                      <Button asChild size="sm" disabled={!r.mobile} className={cn(done ? "bg-secondary text-foreground hover:bg-secondary/80" : "bg-emerald-600 text-white hover:bg-emerald-700")} data-testid={`bulk-send-${r.fe_id}`}>
                        <a href={r.mobile ? waUrl(r.mobile, message(r, s, range)) : undefined} target="_blank" rel="noreferrer" onClick={() => r.mobile && mark(r.id, true)}>
                          {done ? <><CheckCircle2 className="mr-1 h-4 w-4" /> Sent</> : <><MessageCircle className="mr-1 h-4 w-4" /> Send</>}
                        </a>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">"Sent" ticks are saved in this browser for each week so you can pick up where you left off.</p>
    </div>
  );
}
